import type { CardDef } from '../types.js';
import { createCardElement } from './CardView.js';
import { getEffectDescription } from '../engine/EffectText.js';

const CEREMONY_HOLD_MS = 1000;
const BANNER_HOLD_MS = 1000;
const CEREMONIES_LS_KEY = 'cardverse-ceremonies';

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function getInitials(name: string): string {
  return name.split(/\s+/).map(w => w[0] ?? '').join('').slice(0, 3).toUpperCase();
}

/** localStorage flag for the "advanced players" off-switch. Default on. The
 *  toggle UI hasn't been built yet — power users can set it manually via
 *  `localStorage.setItem('cardverse-ceremonies', 'off')`. */
export function ceremoniesEnabled(): boolean {
  try {
    return localStorage.getItem(CEREMONIES_LS_KEY) !== 'off';
  } catch {
    return true;
  }
}

/** Center-screen reveal for an action-card play. Fades in a darkening
 *  backdrop and pops the card forward, holds long enough to read it
 *  (~700ms), then dismisses. Resolves when the dismiss animation has
 *  fully played out so callers can sequence the post-play render afterward.
 *
 *  Action cards never live on the board — without this ceremony the only
 *  signal that something was played is the SP delta and whatever effect
 *  flyaways follow, which leaves opponents thinking "wait, what just
 *  happened?". The ceremony is purely visual; toggling it off via
 *  `ceremoniesEnabled` returns immediately without affecting game state. */
export async function playActionCardCeremony(def: CardDef): Promise<void> {
  if (!ceremoniesEnabled()) return;

  return new Promise<void>(resolve => {
    const overlay = document.createElement('div');
    overlay.className = 'card-ceremony-overlay';

    const host = document.createElement('div');
    host.className = 'card-ceremony-host';

    // suppressTooltip=true: we don't want the mid-ceremony card to spawn its
    // own tooltip. The card is already large and central — a tooltip on top
    // would be redundant noise.
    const cardEl = createCardElement(def, undefined, false, undefined, false, 0, false, true);
    host.appendChild(cardEl);
    overlay.appendChild(host);
    document.body.appendChild(overlay);

    let dismissed = false;
    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      overlay.classList.add('dismissing');
      const onEnd = () => {
        overlay.remove();
        resolve();
      };
      // Tied to the host's exit animation (the longer of the two), not the
      // backdrop fade — we want the resolve to land after the card has
      // visibly left the screen.
      host.addEventListener('animationend', onEnd, { once: true });
    };

    // Click-to-skip. Players who already know what was played can tap the
    // backdrop to advance. Without this the ceremony feels mandatory and
    // gets annoying on plays you've seen 50 times.
    overlay.addEventListener('click', dismiss);

    setTimeout(dismiss, CEREMONY_HOLD_MS);
  });
}

/** Center-screen reveal for a SUPPORT play. Same animation envelope as
 *  `playActionCardCeremony`, but the card body is stripped down: art and
 *  name only, with a "Support: +N Power" line standing in for ability /
 *  stats / power badge / spirit cost. None of those values are meaningful
 *  when a card is consumed as a support, and showing the regular hand-card
 *  body just confuses the player ("why is this character's full ability
 *  text on screen when they're being used to buff someone else?"). */
export async function playSupportCardCeremony(def: CardDef, bonus: number): Promise<void> {
  if (!ceremoniesEnabled()) return;

  return new Promise<void>(resolve => {
    const overlay = document.createElement('div');
    overlay.className = 'card-ceremony-overlay';

    const host = document.createElement('div');
    host.className = 'card-ceremony-host';

    // Reuse the standard .card frame so the rarity gem, border, and
    // ceremony-host size animation all match the action-card path —
    // just swap the inner body for a support-specific layout.
    const cardEl = document.createElement('div');
    cardEl.className = 'card';
    cardEl.dataset.cardId = def.id;
    cardEl.dataset.rarity = def.rarity;
    const initials = getInitials(def.name);
    const artHtml = !def.art
      ? `<span class="placeholder-art">${initials}</span>`
      : `<img src="${def.art}" alt="${escapeHtml(def.name)}" draggable="false" onerror="this.style.display='none';this.nextElementSibling.style.display=''" /><span class="placeholder-art" style="display:none">${initials}</span>`;
    cardEl.innerHTML = `
      <div class="card-art">${artHtml}<div class="card-rarity-gem"></div></div>
      <div class="card-name">${escapeHtml(def.name)}</div>
      <div class="card-ability-text">Support: +${bonus} Power</div>
    `;
    host.appendChild(cardEl);
    overlay.appendChild(host);
    document.body.appendChild(overlay);

    let dismissed = false;
    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      overlay.classList.add('dismissing');
      host.addEventListener('animationend', () => {
        overlay.remove();
        resolve();
      }, { once: true });
    };

    overlay.addEventListener('click', dismiss);
    setTimeout(dismiss, CEREMONY_HOLD_MS);
  });
}

export interface CharacterAbilityBannerOptions {
  /** When true, the overlay's full-viewport area becomes click-through
   *  (`pointer-events: none`) so the player can interact with the game
   *  underneath while the banner is still on screen. The banner box
   *  itself stays clickable for the explicit dismiss gesture. Used for
   *  player-initiated triggers (PLAY_CHARACTER / ACTIVATE_ABILITY on
   *  the player's own turn) where blocking input until the banner
   *  finishes makes follow-up actions — playing an action card,
   *  triggering an ability, staging an attack, ending the turn — feel
   *  laggy. Opponent triggers and automatic ones (on-attack,
   *  end-of-turn, etc.) keep the default blocking behavior so the
   *  player has a beat to read the cause before the game continues. */
  clickThrough?: boolean;
}

/** Banner-style call-out for a character's on-play ability. The character
 *  itself is already on the board so we don't re-show the full card —
 *  just a thumbnail of the art (to anchor the connection to the played
 *  card), the character name, and the rules text. The trigger prefix
 *  ("On play: ") is dropped because the banner appearing IS the on-play
 *  signal — repeating it adds noise.
 *
 *  Resolves once the dismiss animation has played out so callers can
 *  sequence post-banner work (e.g. effect-flyaway popups). */
export async function playCharacterAbilityBanner(
  def: CardDef,
  opts: CharacterAbilityBannerOptions = {},
): Promise<void> {
  if (!ceremoniesEnabled()) return;
  if (def.type !== 'character' || !def.ability) return;

  const ability = def.ability;
  const desc = getEffectDescription(ability.effect, ability.intensity ?? 0, 'action', ability.target);

  return new Promise<void>(resolve => {
    const overlay = document.createElement('div');
    overlay.className = 'ability-banner-overlay';
    if (opts.clickThrough) overlay.classList.add('click-through');

    const banner = document.createElement('div');
    banner.className = 'ability-banner';

    const initials = getInitials(def.name);
    const artHtml = def.art
      ? `<img src="${def.art}" alt="${escapeHtml(def.name)}" draggable="false" onerror="this.style.display='none';this.nextElementSibling.style.display=''" /><span class="ability-banner-art-fallback" style="display:none">${initials}</span>`
      : `<span class="ability-banner-art-fallback">${initials}</span>`;

    banner.innerHTML = `
      <div class="ability-banner-art">${artHtml}</div>
      <div class="ability-banner-text">
        <div class="ability-banner-name">${escapeHtml(def.name)}</div>
        <div class="ability-banner-desc">${escapeHtml(desc)}</div>
      </div>
    `;
    overlay.appendChild(banner);
    document.body.appendChild(overlay);

    let dismissed = false;
    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      overlay.classList.add('dismissing');
      banner.addEventListener('animationend', () => {
        overlay.remove();
        resolve();
      }, { once: true });
    };

    // In default (blocking) mode, the whole overlay is the click target
    // so tapping anywhere on screen dismisses. In click-through mode the
    // overlay doesn't receive pointer events at all (the CSS modifier
    // sets pointer-events:none), so attach the dismiss handler to the
    // banner box itself — players can still tap the banner to dismiss
    // early, but the rest of the screen passes clicks through to game
    // UI for snappier follow-up actions.
    (opts.clickThrough ? banner : overlay).addEventListener('click', dismiss);
    setTimeout(dismiss, BANNER_HOLD_MS);
  });
}
